import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PenLine, X, CheckCircle2, XCircle, AlertTriangle, Umbrella, ShieldAlert, KeyRound } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const formatDate = (s) => (s ? new Date(s + 'T00:00:00').toLocaleDateString('pl-PL') : '');
const formatDateTime = (s) => (s ? new Date(s).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' }) : '');
const fmtDays = (d) => (d == null ? '–' : `${Math.round(d * 100) / 100} dni`);

/** Kadry → Wnioski do podpisu: przełożony (lub jego zastępca) podpisuje wnioski kodem z aplikacji. */
const LeaveSupervisorInbox = () => {
  const [items, setItems] = useState([]);
  const [mfaReady, setMfaReady] = useState(true);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [code, setCode] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/kadry/urlopy/do-podpisu');
      setItems(res.data.items || []);
      setMfaReady(res.data.mfaReady !== false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Nie udało się pobrać wniosków');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setCode('');
    setComment('');
    try {
      const res = await api.get(`/kadry/urlopy/do-podpisu/${id}`);
      setDetail(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Nie udało się pobrać wniosku');
    } finally {
      setDetailLoading(false);
    }
  };

  const sign = async () => {
    if (!detail || code.trim().length < 6) {
      toast.error('Wpisz 6-cyfrowy kod z aplikacji');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/kadry/urlopy/${detail.request.id}/podpisz`, { code: code.trim(), comment: comment || null });
      toast.success('Wniosek podpisany i przekazany do kadr');
      setDetail(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Nie udało się podpisać wniosku');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      await api.post(`/kadry/urlopy/${detail.request.id}/odrzuc-przelozony`, { comment: comment || null });
      toast.success('Wniosek odrzucony');
      setDetail(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Nie udało się odrzucić wniosku');
    } finally {
      setBusy(false);
    }
  };

  const r = detail?.request;
  const v = detail?.validation;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center shadow-md">
          <PenLine className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 leading-tight">Wnioski do podpisu</h1>
          <p className="text-sm text-gray-500">Wnioski urlopowe Twoich pracowników (także w zastępstwie)</p>
        </div>
      </div>

      {!mfaReady && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 flex items-start gap-3 text-sm text-amber-900">
          <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <span>
            Aby podpisywać wnioski, skonfiguruj podpis elektroniczny (kod z aplikacji uwierzytelniającej):{' '}
            <Link to="/app/mfa-settings" className="font-semibold underline">Podpis elektroniczny</Link>.
          </span>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center h-40"><LoadingSpinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">Brak wniosków czekających na Twój podpis.</div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                {['Pracownik', 'Rodzaj', 'Termin', 'Dni rob.', 'Złożono'].map((h) => (
                  <th key={h} className="px-4 py-2 text-left text-[11px] font-semibold tracking-wider text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((it) => (
                <tr key={it.id} onClick={() => openDetail(it.id)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-3 text-sm text-gray-900">{it.userName}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{it.kindName}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDate(it.dateFrom)} – {formatDate(it.dateTo)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{it.daysEstimate ?? '–'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDateTime(it.submittedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(detail || detailLoading) && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50" onClick={() => !busy && setDetail(null)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-xl p-6">
              <button onClick={() => setDetail(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" aria-label="Zamknij">
                <X className="w-5 h-5" />
              </button>
              {detailLoading || !r ? (
                <div className="flex justify-center items-center h-40"><LoadingSpinner size="lg" /></div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{r.userName}</h2>
                    <p className="text-sm text-gray-600">
                      {r.kindName}: <b>{formatDate(r.dateFrom)} – {formatDate(r.dateTo)}</b>
                      {r.daysEstimate != null && <> · {r.daysEstimate} dni rob.</>}
                    </p>
                    {r.comment && <p className="text-sm text-gray-500 mt-1">Komentarz pracownika: {r.comment}</p>}
                  </div>

                  {detail.balance?.contracts?.length > 0 && (
                    <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 flex items-start gap-3">
                      <Umbrella className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="text-sm text-gray-700">
                        {detail.balance.contracts.map((c) => (
                          <div key={c.idUmp}>
                            Bilans w KS-ZZL: pozostało <b>{fmtDays(c.pozostalyDni)}</b> z należnych {fmtDays(c.naleznyDni)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {v && (
                    v.ok ? (
                      <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-sm bg-emerald-50 text-emerald-800 border border-emerald-200">
                        <CheckCircle2 className="w-4 h-4 mt-0.5" /> Kontrola KS-ZZL: bez zastrzeżeń.
                      </div>
                    ) : (
                      (v.errors || []).map((e, i) => (
                        <div key={i} className="flex items-start gap-2 rounded-lg px-3 py-2 text-sm bg-red-50 text-red-800 border border-red-200">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{e}</span>
                        </div>
                      ))
                    )
                  )}

                  <div>
                    <label htmlFor="supComment" className="block text-sm text-gray-700 mb-1.5">
                      Komentarz <span className="text-gray-400 text-xs">(opcjonalny, widzi pracownik i kadry)</span>
                    </label>
                    <textarea
                      id="supComment"
                      rows={2}
                      maxLength={500}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {mfaReady && (
                    <div>
                      <label htmlFor="supCode" className="block text-sm text-gray-700 mb-1.5">Kod z aplikacji uwierzytelniającej</label>
                      <div className="relative">
                        <KeyRound className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                          id="supCode"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                          onKeyDown={(e) => { if (e.key === 'Enter') sign(); }}
                          className="w-40 pl-9 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm tracking-[0.3em] font-mono text-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder="000000"
                          autoFocus
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap justify-end gap-2 pt-1">
                    <button disabled={busy} onClick={reject} className="btn btn-secondary flex items-center gap-2">
                      <XCircle className="w-4 h-4" /> Odrzuć
                    </button>
                    <button
                      disabled={busy || !mfaReady || code.length < 6}
                      onClick={sign}
                      className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {busy ? <LoadingSpinner size="sm" /> : <PenLine className="w-4 h-4" />}
                      Podpisz i przekaż do kadr
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveSupervisorInbox;
